import { useCallback } from 'react';
import config from '@app/config';
import logger from '@app/libs/logger';

// Define the interface for the geocoding result
export interface GeocodingResult {
  geometry: {
    viewport: {
      northeast: {
        lat: number;
        lng: number;
      };
      southwest: {
        lat: number;
        lng: number;
      };
    };
  };
  [key: string]: any; // For other properties in the result that we might not be explicitly using
}

export const useGeocoding = (): {
  getGeocodeData: (address: string, componentRestrictions?: { country: string }) => Promise<GeocodingResult[]>;
} => {
  const getGeocodeData = useCallback(
    async (address: string, componentRestrictions?: { country: string }): Promise<GeocodingResult[]> => {
      const fetchGoogleGeocodingResponse = async (address: string, componentRestrictions?: { country: string }): Promise<any> => {
        const params = new URLSearchParams({
          address,
          key: config.googleGeocodingApiKey ?? '',
        });
        if (componentRestrictions?.country) {
          params.append('components', `country:${componentRestrictions.country}`);
        }
        const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?${params}`);
        return await response.json();
      };

      const extractResult = (data: any): GeocodingResult[] => {
        if (data.status === 'OK' && data.results.length > 0) {
          return data.results;
        } else {
          throw new Error(`Geocoding failed: ${data.status}`);
        }
      };

      try {
        const data = await fetchGoogleGeocodingResponse(address, componentRestrictions);
        return extractResult(data);
      } catch (error) {
        logger.exception(error, { address, componentRestrictions });
        throw error;
      }
    },
    [],
  );

  return { getGeocodeData };
};
